import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ProductMediaPage.css';
import '../../../shared/ListRowHover.css';
import '../../../shared/StyledInput.css';
import PageHeader from '../../../shared/PageHeader';
import BrowseNav, { useBrowseIds } from '../../../shared/BrowseNav';
import { useErrorToast, useDismissError } from '../../../shared/ErrorToastContext';
import { mediaFileUrl as fileUrl, thumbFileUrl } from '../../../shared/mediaFileUrl';
import EditableFilename from '../../../shared/EditableFilename';
import { askConfirm } from '../../../shared/AppDialogs';

const CATEGORIES = [
  { key: 'marketing', label: 'Marketing Photos', accept: '.jpg,.jpeg,.png', hint: 'JPEG or PNG' },
  { key: 'technical', label: 'Technical Drawings', accept: '.png,.svg,application/pdf', hint: 'PNG, SVG, or PDF (reference only, never primary)' },
];

function MediaThumb({ item }) {
  if (item.mime_type === 'application/pdf') {
    return <a className="media-thumb-pdf" href={fileUrl(item.media_image_id)} target="_blank" rel="noreferrer">PDF</a>;
  }
  return <img className="media-thumb" src={thumbFileUrl(item.media_image_id)} alt={item.original_filename} />;
}

function UploadForm({ category, onUpload }) {
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [file, setFile] = useState(null);
  const [isPrimary, setIsPrimary] = useState(false);
  const [saving, setSaving] = useState(false);

  const isPdf = file && file.type === 'application/pdf';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return showError(null, 'Choose a file to upload.');
    setSaving(true);
    try {
      await onUpload(file, isPrimary && !isPdf);
      setFile(null);
      setIsPrimary(false);
      e.target.reset();
      dismissError();
    } catch (err) {
      showError(err, 'Could not upload image');
    }
    setSaving(false);
  };

  return (
    <div className="media-action-group">
      <div className="media-action-heading">Upload a new image</div>
      <form onSubmit={handleSubmit}>
        <div className="media-action-row">
          <input type="file" onChange={(e) => setFile(e.target.files[0] || null)} />
        </div>
        <div className="media-action-row media-action-confirm">
          <label className="media-action-checkbox">
            <input type="checkbox" checked={isPrimary} disabled={isPdf} onChange={(e) => setIsPrimary(e.target.checked)} title={isPdf ? 'A PDF cannot be marked primary' : undefined} /> Set as primary
          </label>
          <button type="submit" className="primary-button" disabled={saving || !file}>{saving ? 'Uploading...' : '+ Upload New'}</button>
        </div>
      </form>
    </div>
  );
}

function AttachExistingPicker({ available, onAttach }) {
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState('');
  const [isPrimary, setIsPrimary] = useState(false);
  const filtered = available.filter((img) => img.original_filename.toLowerCase().includes(search.toLowerCase()));
  const selected = available.find((img) => String(img.id) === selectedId);
  const isPdf = selected && !selected.primary_eligible;

  const handleAttach = async () => {
    if (!selectedId) return;
    await onAttach(Number(selectedId), isPrimary && !isPdf);
    setSelectedId('');
    setIsPrimary(false);
  };

  if (!available.length) return null;

  return (
    <div className="media-action-group">
      <div className="media-action-heading">Attach an existing image</div>
      <div className="media-action-row">
        <input className="styled-input" type="text" placeholder="Search library..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ width: 160 }} />
        <select className="styled-input" value={selectedId} onChange={(e) => setSelectedId(e.target.value)} style={{ width: 220 }}>
          <option value="">(select an existing image)</option>
          {filtered.map((img) => (
            <option key={img.id} value={img.id}>{img.original_filename}</option>
          ))}
        </select>
      </div>
      <div className="media-action-row media-action-confirm">
        <label className="media-action-checkbox">
          <input type="checkbox" checked={isPrimary} disabled={!selectedId || isPdf} onChange={(e) => setIsPrimary(e.target.checked)} title={isPdf ? 'A PDF cannot be marked primary' : undefined} /> Set as primary
        </label>
        <button type="button" className="primary-button" disabled={!selectedId} onClick={handleAttach}>+ Attach Existing</button>
      </div>
    </div>
  );
}

function CategorySection({ category, label, hint, attachments, available, onUpload, onAttachExisting, onSetPrimary, onDetach, onRename }) {
  return (
    <div style={{ background: '#fff', borderRadius: 8, padding: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', marginBottom: 16 }}>
      <h4 style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 600, color: '#8a94a6', textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</h4>
      <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{hint}</div>

      {attachments.length === 0 ? (
        <div style={{ fontSize: 13, color: '#888' }}>No {label.toLowerCase()} attached — the generic placeholder is used wherever this would show.</div>
      ) : (
        <div className="media-grid">
          {attachments.map((item) => (
            <div key={item.id} className={`media-card${item.is_primary ? ' is-primary' : ''}`}>
              <MediaThumb item={item} />
              <EditableFilename
                filename={item.original_filename}
                onSave={(newName) => onRename(item.media_image_id, newName)}
                style={{ fontSize: 12, color: '#344054', lineHeight: 1.3, whiteSpace: 'normal', wordBreak: 'break-word', overflow: 'visible', textOverflow: 'clip', width: '100%' }}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                {item.primary_eligible ? (
                  <label style={{ fontSize: 11, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <input type="radio" name={`primary-${category}`} checked={item.is_primary} onChange={() => onSetPrimary(item.id)} />
                    Primary
                  </label>
                ) : <span style={{ fontSize: 11, color: '#aaa' }}>Reference only</span>}
                <button type="button" className="action-button" style={{ color: '#b91c1c', background: '#fef2f2', borderColor: '#fecaca' }} onClick={() => onDetach(item.id)}>Detach</button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="media-actions">
        <AttachExistingPicker available={available} onAttach={(mediaImageId, isPrimary) => onAttachExisting(mediaImageId, isPrimary)} />
        <UploadForm category={category} onUpload={(file, isPrimary) => onUpload(file, isPrimary)} />
      </div>
    </div>
  );
}

// Product Media page — the Media Library's Product-facing side
// (vis_product_media_library.md): what's attached, uploading a brand-new
// image straight onto this Product, and attaching an image that already
// exists in the library (the SP400/SP400BK shared-drawing case). Two
// independent sections (Technical/Marketing) since the categories answer
// different questions and are never substitutes for each other.
function ProductMediaPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const browseIds = useBrowseIds();
  const showError = useErrorToast();
  const dismissError = useDismissError();
  const [product, setProduct] = useState(null);
  const [attachments, setAttachments] = useState([]);
  const [available, setAvailable] = useState({ technical: [], marketing: [] });
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const fetchAll = async () => {
    try {
      const [mediaRes, productRes, techRes, mktRes] = await Promise.all([
        axios.get(`/api/products/${id}/media`),
        axios.get(`/api/products/${id}`),
        axios.get('/api/media-images', { params: { category: 'technical', exclude_product_id: id } }),
        axios.get('/api/media-images', { params: { category: 'marketing', exclude_product_id: id } }),
      ]);
      setAttachments(mediaRes.data.data);
      setProduct(productRes.data.data);
      setAvailable({ technical: techRes.data.data, marketing: mktRes.data.data });
      setLoadError('');
    } catch (err) {
      setLoadError(err.response?.data?.message || 'Could not load Product Media');
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line
  }, [id]);

  if (loading) return <div style={{ padding: 24 }}>Loading...</div>;
  if (loadError) return <div style={{ padding: 24, color: 'red' }}>{loadError}</div>;
  if (!product) return null;

  const handleUpload = async (category, file, isPrimary) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('category', category);
    if (isPrimary) formData.append('is_primary', 'true');
    await axios.post(`/api/products/${id}/media/upload`, formData);
    await fetchAll();
  };

  const handleAttachExisting = async (mediaImageId, isPrimary) => {
    try {
      await axios.post(`/api/products/${id}/media`, { media_image_id: mediaImageId, is_primary: !!isPrimary });
      await fetchAll();
      dismissError();
    } catch (err) {
      showError(err, 'Could not attach image');
    }
  };

  const handleSetPrimary = async (attachmentId) => {
    try {
      await axios.patch(`/api/products/${id}/media/${attachmentId}`, { is_primary: true });
      await fetchAll();
      dismissError();
    } catch (err) {
      showError(err, 'Could not mark as primary');
    }
  };

  const handleRename = async (mediaImageId, newFilename) => {
    await axios.patch(`/api/media-images/${mediaImageId}`, { original_filename: newFilename });
    await fetchAll();
  };

  const handleDetach = async (attachmentId) => {
    if (!(await askConfirm('Detach this image from the product? The image itself stays in the Media Library.', { confirmLabel: 'Detach' }))) return;
    try {
      await axios.delete(`/api/products/${id}/media/${attachmentId}`);
      await fetchAll();
      dismissError();
    } catch (err) {
      showError(err, 'Could not detach image');
    }
  };

  return (
    <div className="product-media-page" style={{ width: '100%', maxWidth: 1100, margin: '32px auto 48px', background: '#f6f8fa', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.07)', padding: '20px 24px 32px' }}>
      <PageHeader
        crumbs={[
          { label: 'Products', onClick: () => navigate('/products') },
          { label: product.sku },
          { label: 'Product Media' },
        ]}
        title="Product Media"
        subtitle={<strong>{product.sku} — {product.name}</strong>}
        meta={(
          <>
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/uom`, { state: browseIds ? { browseIds } : undefined })}>Product Units of Measure →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/sales-prices?product_id=${id}`)}>Prices →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products/${id}/weights`, { state: browseIds ? { browseIds } : undefined })}>Weights & Dimensions →</button>
            {' · '}
            <button type="button" className="link-button" onClick={() => navigate(`/products?edit=${id}`)}>Edit on Product</button>
          </>
        )}
      />

      {CATEGORIES.map((cat) => (
        <CategorySection
          key={cat.key}
          category={cat.key}
          label={cat.label}
          hint={cat.hint}
          attachments={attachments.filter((a) => a.category === cat.key)}
          available={available[cat.key]}
          onUpload={(file, isPrimary) => handleUpload(cat.key, file, isPrimary)}
          onAttachExisting={handleAttachExisting}
          onSetPrimary={handleSetPrimary}
          onDetach={handleDetach}
          onRename={handleRename}
        />
      ))}

      <BrowseNav id={id} ids={browseIds} buildPath={(targetId) => `/products/${targetId}/media`} noun="Products" />
    </div>
  );
}

export default ProductMediaPage;
